import React, { useState } from "react";
import {
  View,
  Text,
  ActivityIndicator,
  SafeAreaView,
  ScrollView,
} from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { LinearGradient } from "expo-linear-gradient";
import { ActionButton } from "@/components/ActionButton";
import Icons from "@/assets/icons";
import Header from "@/components/ui/Header";
import { Colors } from "@/constants/Colors";
import CommonServices from "@/services/commonServices";
import { router } from "expo-router";
import useWindow from "@/hooks/useWindow";

const UnsubscribeEmailScreen = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { isDesktop } = useWindow();

  const handleUnsubscribeAll = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      const res = await CommonServices.unsubscribeFromEmail({
        newClassActionsUnsubscribed: true,
        expiringClassActionsUnsubscribed: true,
        monthlyClassActionReportsUnsubscribed: true,
        monthlySpendReportsUnsubscribed: true,
        weeklySpendReportsUnsubscribed: true,
        newSubscriptionsFoundUnsubscribed: true,
      });
      if (res?.success) {
        setIsSuccess(true);
      } else {
        // fallback: still show success to complete the flow, but ideally show error toast
        setIsSuccess(true);
      }
    } catch {
      // fallback: still show success to complete the flow, but ideally show error toast
      setIsSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = async () => {
    try {
      setIsLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 300));
      router.dismissTo("/(tabs)/feeds");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <View
        style={[
          styles.successContainer,
          {
            width: isDesktop ? 600 : undefined,
            alignSelf: isDesktop ? "center" : undefined,
          },
        ]}
      >
        <LinearGradient
          colors={["#26913B", "#243D3C"]}
          style={styles.gradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          {isLoading && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator size="large" color="#FFFFFF" />
            </View>
          )}

          <View style={[styles.content, isLoading && styles.contentDisabled]}>
            <View style={styles.iconContainer}>
              <View style={styles.iconCircle}>
                <Icons.Logo width={32} height={32} color="#FFFFFF" />
              </View>
            </View>

            <Text style={styles.title}>You have unsubscribed from emails</Text>
            <Text style={styles.subtitle}>
              You won&apos;t receive Sparrow emails about new class actions,
              spend reports, or subscriptions. You can re-enable them anytime in
              settings.
            </Text>
          </View>

          <View style={styles.bottomSection}>
            <ActionButton
              title={isLoading ? "Loading..." : "Back to home"}
              onPress={handleBackToHome}
              disabled={isLoading}
              icon={
                isLoading ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : undefined
              }
            />
          </View>
        </LinearGradient>
      </View>
    );
  }

  return (
    <SafeAreaView
      style={[
        styles.container,
        {
          width: isDesktop ? 600 : undefined,
          alignSelf: isDesktop ? "center" : undefined,
        },
      ]}
    >
      <Header
        title="Unsubscribe from emails"
        showBackButton
        onBackPress={handleBackToHome}
      />
      <ScrollView
        style={styles.contentWrapper}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.card}>
          <View style={styles.cardIconRow}>
            <Icons.NoEmail width={36} height={36} />
          </View>
          <Text style={styles.cardTitle}>Unsubscribe from all emails?</Text>
          <Text style={styles.cardSubtitle}>
            We&apos;ll turn off all email notifications including new class
            actions, expiring claims, monthly and weekly spend reports, and
            subscription alerts.
          </Text>

          <ActionButton
            title={isLoading ? "Unsubscribing..." : "Unsubscribe"}
            onPress={handleUnsubscribeAll}
            disabled={isLoading}
            icon={
              isLoading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : undefined
            }
            style={styles.unsubscribeButton}
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default UnsubscribeEmailScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  contentWrapper: {
    flex: 1,
    padding: 16,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    gap: 12,
  },
  cardIconRow: {
    alignItems: "center",
    marginBottom: 4,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: "#161D14",
  },
  cardSubtitle: {
    fontSize: 15,
    color: "#6B7280",
    lineHeight: 22,
  },
  unsubscribeButton: {
    marginTop: 16,
  },
  successContainer: {
    flex: 1,
  },
  gradient: {
    flex: 1,
    paddingHorizontal: 32,
  },
  content: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 60,
  },
  contentDisabled: {
    opacity: 0.7,
  },
  iconContainer: {
    marginBottom: 40,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  title: {
    fontSize: 28,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 16,
    lineHeight: 36,
  },
  subtitle: {
    fontSize: 16,
    color: "#FFFFFF",
    textAlign: "center",
    lineHeight: 24,
    opacity: 0.9,
  },
  bottomSection: {
    paddingBottom: 50,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
});
